import { useLogin } from 'react-admin';
import { useEffect, useState } from 'react';
import { Box, Card, CardContent, Typography, Button, Container } from '@mui/material';
import LoginIcon from '@mui/icons-material/Login';
import { AdminPanelMark, ADMINPANEL_PRIMARY, ADMINPANEL_PRIMARY_DARK } from './components/AdminPanelBrand';

/**
 * Login-Zwischenseite des Admins — normalerweise nur kurz sichtbar, weil der
 * Auto-Login sofort zum SSO weiterleitet. Design analog zum generation:l-Login
 * des SSO (2026-09), gebrandet mit der admin:panel-Zwischenmarke: heller Grund,
 * weisse Karte, Produkt-Signet, gruener Primaer-Button, keine Gradients.
 */
const LoginPage = () => {
    const login = useLogin();

    const wasLoggedOut = localStorage.getItem('user_logged_out') === 'true';

    const [showLogoutMessage] = useState(wasLoggedOut);
    const [autoLoggingIn, setAutoLoggingIn] = useState(!wasLoggedOut);

    useEffect(() => {
        if (!wasLoggedOut) {
            localStorage.removeItem('user_logged_out');
            login({}).catch(() => {
                localStorage.removeItem('auto_login_in_progress');
                setAutoLoggingIn(false);
            });

            // Safety timeout: if redirect to SSO doesn't happen within 8 seconds,
            // show the manual login button so the user isn't stuck on "Anmeldung läuft..."
            const timeout = setTimeout(() => {
                localStorage.removeItem('auto_login_in_progress');
                setAutoLoggingIn(false);
            }, 8000);

            return () => clearTimeout(timeout);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Run once on mount - auto-login must not re-trigger on every render

    const handleLogin = () => {
        localStorage.removeItem('user_logged_out');
        login({});
    };

    const primaryButtonSx = {
        mt: 3,
        py: '14px',
        fontSize: '16px',
        fontWeight: 600,
        borderRadius: '6px',
        backgroundColor: ADMINPANEL_PRIMARY,
        boxShadow: 'none',
        '&:hover': {
            backgroundColor: ADMINPANEL_PRIMARY_DARK,
            boxShadow: '0 4px 16px rgba(0, 0, 0, 0.12)',
        },
    };

    return (
        <Box
            sx={{
                minHeight: '100vh',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: '#f4f4f4',
            }}
        >
            <Container maxWidth="xs">
                <Card elevation={0} sx={{ borderRadius: '12px', boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)' }}>
                    <CardContent sx={{ p: 4 }}>
                        <Box sx={{ display: 'flex', justifyContent: 'center', mb: 3 }}>
                            <AdminPanelMark height={96} />
                        </Box>

                        {autoLoggingIn ? (
                            <>
                                <Typography variant="h5" align="center" gutterBottom sx={{ fontWeight: 600, color: '#1f3240', mb: 2 }}>
                                    Anmeldung läuft...
                                </Typography>
                                <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 3 }}>
                                    Sie werden automatisch angemeldet
                                </Typography>
                                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                    <div style={{
                                        width: '40px',
                                        height: '40px',
                                        border: '4px solid #e1e2e4',
                                        borderTop: `4px solid ${ADMINPANEL_PRIMARY}`,
                                        borderRadius: '50%',
                                        animation: 'spin 1s linear infinite',
                                    }}></div>
                                </Box>
                                <style>{`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}</style>
                            </>
                        ) : showLogoutMessage ? (
                            <>
                                <Typography variant="h5" align="center" gutterBottom sx={{ fontWeight: 600, color: '#1f3240', mb: 2 }}>
                                    Erfolgreich abgemeldet
                                </Typography>
                                <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 1 }}>
                                    Ihre lokale Session wurde beendet.
                                </Typography>
                                <Typography variant="body2" align="center" color="text.secondary">
                                    Melden Sie sich erneut an, um fortzufahren.
                                </Typography>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    onClick={handleLogin}
                                    startIcon={<LoginIcon />}
                                    sx={primaryButtonSx}
                                >
                                    Mit SSO anmelden
                                </Button>
                            </>
                        ) : (
                            <>
                                <Typography variant="h5" align="center" gutterBottom sx={{ fontWeight: 600, color: '#1f3240', mb: 2 }}>
                                    Willkommen
                                </Typography>
                                <Typography variant="body1" align="center" color="text.secondary" sx={{ mb: 1 }}>
                                    admin:panel — Verwaltung der generation:l-Produkte
                                </Typography>
                                <Typography variant="body2" align="center" color="text.secondary">
                                    Melden Sie sich mit Ihrem SSO-Account an
                                </Typography>
                                <Button
                                    fullWidth
                                    variant="contained"
                                    onClick={handleLogin}
                                    startIcon={<LoginIcon />}
                                    sx={primaryButtonSx}
                                >
                                    Mit SSO anmelden
                                </Button>
                            </>
                        )}
                    </CardContent>
                </Card>
            </Container>
        </Box>
    );
};

export default LoginPage;
